import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Trophy, Calendar, Target, ChartLine } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { getApiUrl } from "../../config/api";
import { useSubscription } from '../../hooks/useSubscription';

export default function YearToDateStatsWidget({ config = {} }) {
  const [stats, setStats] = useState(null);
  const [prevStats, setPrevStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [familyContacts, setFamilyContacts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedContactId, setSelectedContactId] = useState('all');
  const { hasFeature } = useSubscription();

  // Parse a YYYY-MM-DD string into a local Date (avoids UTC timezone shifts)
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [y, m, d] = dateString.split('-').map((x) => parseInt(x, 10));
    if (!y || !m || !d) return new Date(dateString);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  };

  const parseMaybeLocalDate = (value) => {
    if (!value) return null;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return parseLocalDate(value);
    }
    return new Date(value);
  };

  const {
    title = 'YTD Results Stats',
    showPoints = true,
    showEarnings = true,
    startDate = new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // January 1st of current year
    endDate = new Date().toISOString().split('T')[0], // Today
    showDateRange = true,
    showTrend = true
  } = config;

  useEffect(() => {
    // Load current user and family members for Family plan
    const loadUserAndContacts = async () => {
      try {
        if (!hasFeature('family_plan')) return;
        const token = localStorage.getItem('token');
        const [userRes, familyRes] = await Promise.all([
          axios.get(getApiUrl('/api/profile'), { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(getApiUrl('/api/settings/family-members'), { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setCurrentUser(userRes.data);
        setFamilyContacts(familyRes.data || []);
      } catch (e) {
        // Non-fatal for widget; keep dropdown empty if it fails
        console.error('Error loading user/family for YTD widget:', e);
      }
    };
    loadUserAndContacts();
  }, [hasFeature]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all results, filter by show dates client-side for accuracy
        const response = await axios.get(getApiUrl('/api/results'));
        
        if (response.data) {
          let results = response.data || [];
          // Client-side filter by family member when on Family plan
          if (hasFeature('family_plan')) {
            if (selectedContactId === 'current_user') {
              results = results.filter(r => !r.family_member_id);
            } else if (selectedContactId !== 'all') {
              results = results.filter(r => String(r.family_member_id) === String(selectedContactId));
            }
          }
          // Helper to get show date from various fields
          const extractShowDate = (r) => {
            const showDate = r.show_date || r.show_start_date || r.first_event_date;
            const resultDataDate = r.result_data?.ShowDate || r.result_data?.date;
            return showDate || resultDataDate || r.created_at;
          };

          // Filter for current period by show dates
          const start = parseLocalDate(startDate);
          const end = parseLocalDate(endDate);
          const inCurrentRange = results.filter((r) => {
            const d = extractShowDate(r);
            if (!d) return false;
            const dt = parseMaybeLocalDate(d);
            const endOfDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);
            return dt >= start && dt <= endOfDay;
          });

          // Build robust earnings/points calculators
          const sumEventEarnings = (res) => {
            const events = res.result_data?.events;
            if (Array.isArray(events) && events.length > 0) {
              return events.reduce((evSum, ev) => {
                if (ev.earnings != null) return evSum + (parseFloat(ev.earnings) || 0);
                const draws = Array.isArray(ev.draws) ? ev.draws : [];
                const drawEarnings = draws.reduce((dSum, d) => {
                  const contestants = Array.isArray(d.contestants) ? d.contestants : [];
                  return dSum + contestants.reduce((cSum, c) => cSum + (parseFloat(c?.earnings) || 0), 0);
                }, 0);
                return evSum + drawEarnings;
              }, 0);
            }
            // Fallbacks
            if (res.total_earnings != null) return parseFloat(res.total_earnings) || 0;
            const top = res.result_data?.totalEarnings;
            return parseFloat(top) || 0;
          };

          const sumEventPoints = (res) => {
            const events = res.result_data?.events;
            if (Array.isArray(events) && events.length > 0) {
              return events.reduce((evSum, ev) => {
                if (ev.points != null) return evSum + (parseFloat(ev.points) || 0);
                const draws = Array.isArray(ev.draws) ? ev.draws : [];
                const drawPoints = draws.reduce((dSum, d) => {
                  const contestants = Array.isArray(d.contestants) ? d.contestants : [];
                  return dSum + contestants.reduce((cSum, c) => cSum + (parseFloat(c?.points) || 0), 0);
                }, 0);
                return evSum + drawPoints;
              }, 0);
            }
            // Fallbacks
            if (res.total_points != null) return parseFloat(res.total_points) || 0;
            const top = res.result_data?.totalPoints;
            return parseFloat(top) || 0;
          };

          const totalEarnings = inCurrentRange.reduce((sum, r) => sum + sumEventEarnings(r), 0);
          const totalPoints = inCurrentRange.reduce((sum, r) => sum + sumEventPoints(r), 0);
          const totalEvents = inCurrentRange.length;

          // Previous period (same range last year) from same dataset
          const prevStart = parseLocalDate(startDate);
          prevStart.setFullYear(prevStart.getFullYear() - 1);
          const prevEnd = parseLocalDate(endDate);
          prevEnd.setFullYear(prevEnd.getFullYear() - 1);
          const inPrevRange = results.filter((r) => {
            const d = extractShowDate(r);
            if (!d) return false;
            const dt = parseMaybeLocalDate(d);
            const prevEndOfDay = new Date(prevEnd.getFullYear(), prevEnd.getMonth(), prevEnd.getDate(), 23, 59, 59, 999);
            return dt >= prevStart && dt <= prevEndOfDay;
          });
          const prevEarnings = inPrevRange.reduce((sum, r) => sum + sumEventEarnings(r), 0);
          const prevPoints = inPrevRange.reduce((sum, r) => sum + sumEventPoints(r), 0);

          setPrevStats({ points: prevPoints, earnings: prevEarnings });

          const pointsChange = prevPoints > 0 ? ((totalPoints - prevPoints) / prevPoints) * 100 : 0;
          const earningsChange = prevEarnings > 0 ? ((totalEarnings - prevEarnings) / prevEarnings) * 100 : 0;

          setStats({
            totalPoints,
            totalEarnings,
            resultCount: totalEvents,
            trend: { points: pointsChange, earnings: earningsChange }
          });
        }

      } catch (err) {
        console.error('Error fetching year-to-date stats:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [startDate, endDate, showTrend, hasFeature, selectedContactId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center gap-2">
          <ChartLine className="h-5 w-5 text-yellow-400" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center gap-2">
          <ChartLine className="h-5 w-5 text-yellow-400" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="text-red-600 text-sm">Error loading stats: {error}</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ChartLine className="h-5 w-5 text-yellow-400" />  
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        {hasFeature('family_plan') && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">Family:</label>
            <select
              value={selectedContactId}
              onChange={(e) => setSelectedContactId(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value="all">All</option>
              {currentUser && (
                <option value="current_user">{currentUser.name || 'You'} (You)</option>
              )}
              {familyContacts.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {showDateRange && (
        <div className="text-sm text-gray-600 mb-4">
          {format(parseLocalDate(startDate), 'MMM d, yyyy')} - {format(parseLocalDate(endDate), 'MMM d, yyyy')}
        </div>
      )}

      <div className="space-y-4">
        {showPoints && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              <span className="text-gray-700">Total Points</span>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-gray-900">
                {stats.totalPoints.toLocaleString()}
              </div>
              {showTrend && stats.trend && (
                <div className={`text-sm ${stats.trend.points >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stats.trend.points >= 0 ? '+' : ''}{stats.trend.points.toFixed(1)}% vs previous period
                </div>
              )}
            </div>
          </div>
        )}

        {showEarnings && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <span className="text-gray-700">Total Earnings</span>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-gray-900">
                ${stats.totalEarnings.toLocaleString()}
              </div>
              {showTrend && stats.trend && (
                <div className={`text-sm ${stats.trend.earnings >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stats.trend.earnings >= 0 ? '+' : ''}{stats.trend.earnings.toFixed(1)}% vs previous period
                </div>
              )}
            </div>
          </div>
        )}

        <div className="text-sm text-gray-500 text-center">
          {stats.resultCount} results in this period
        </div>
      </div>


    </div>
  );
} 